/**
 * Every way out of the call view, none of which ends the call (docs/ux/voice-call.md, the iOS
 * in-call pill pattern):
 * - the header's Minimise (chevron-down on the phone, × on the desktop dock);
 * - Esc (after call.show, which Esc closes first);
 * - on the phone, where the call fills the screen: browser back and the tab bar. Opening the call
 *   view pushes one history entry, so back pops it instead of leaving the page underneath.
 * Once the call has ended the same exits close its summary.
 */
import { useRouter } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import type { CallApi } from '../../lib/call/CallProvider.js'

const PHONE = '(max-width: 720px)'
const MARK = 'vikoCallView'

export const isPhoneLayout = () =>
  typeof matchMedia === 'function' ? matchMedia(PHONE).matches : false

/** Leave the call view: minimise a running call, close an ended or never-started one. */
export function leaveCallView(c: Pick<CallApi, 'state' | 'minimise' | 'dismiss'>) {
  const p = c.state.phase
  if (p === 'live' || p === 'starting') c.minimise()
  else c.dismiss()
}

export function useCallExits(c: CallApi) {
  const router = useRouter()
  const open = c.state.phase !== 'idle' && c.state.view === 'open'
  const pushed = useRef(false)
  const api = useRef(c)
  api.current = c

  // Esc: leaves the view. A text field keeps its own Esc; call.show and dialogs close first.
  // (A dialog's own keys run in its subtree; an open dialog elsewhere keeps Esc for itself.)
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.metaKey || e.ctrlKey || e.altKey) return
      const t = e.target as HTMLElement | null
      if (t?.closest?.('input, textarea, select, [contenteditable="true"], [role="dialog"]')) return
      if (document.querySelector('dialog[open], [role="dialog"][aria-modal="true"]')) return
      if (api.current.state.show) return // CallShowPane closes first
      e.preventDefault()
      leaveCallView(api.current)
    }
    // Capture: the page's own Esc handlers (list selection, inspectors) must not swallow it.
    addEventListener('keydown', onKey, true)
    return () => removeEventListener('keydown', onKey, true)
  }, [open])

  // Phone: one history entry for the open call view; back (or any navigation) leaves the view.
  useEffect(() => {
    if (!open || !isPhoneLayout()) return
    const h = router.history
    if (!pushed.current) {
      const here = h.location
      h.push(`${here.pathname}${here.search}${here.hash}`, { ...here.state, [MARK]: 1 })
      pushed.current = true
    }
    const unsub = h.subscribe(({ location }: { location: { state?: unknown } }) => {
      if ((location.state as Record<string, unknown> | undefined)?.[MARK]) return
      pushed.current = false
      leaveCallView(api.current)
    })
    return unsub
  }, [open, router])

  // Leaving by the view's own controls pops the entry it pushed (so back isn't a dead press).
  useEffect(() => {
    if (open || !pushed.current) return
    pushed.current = false
    const st = router.history.location.state as Record<string, unknown> | undefined
    if (st?.[MARK]) router.history.back()
  }, [open, router])
}
